import React, { useState } from 'react';
import { PipelineProject, getStages } from '../../types/pipeline';
import { pipelineAPI } from '../../services/api';
import { useUIStore } from '../../stores/uiStore';
import Modal from '../common/Modal';

interface EditProgramModalProps {
  project: PipelineProject;
  onClose: () => void;
  onSaved: () => void;
}

const STATUS_OPTIONS: { value: PipelineProject['status']; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'at_risk', label: 'At Risk' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
];

const EditProgramModal: React.FC<EditProgramModalProps> = ({ project, onClose, onSaved }) => {
  const showNotification = useUIStore((s) => s.showNotification);

  const [name, setName] = useState(project.name);
  const [target, setTarget] = useState(project.target);
  const [therapeuticArea, setTherapeuticArea] = useState(project.indicationCode);
  const [status, setStatus] = useState(project.status);
  const [progress, setProgress] = useState(project.progress);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const stages = getStages();
      const stage = stages.find((s) => s.id === project.stage);

      const payload: Record<string, unknown> = {};
      if (name.trim() && name !== project.name) payload.name = name.trim();
      if (target !== project.target) payload.target_name = target;
      if (therapeuticArea !== project.indicationCode) payload.therapeutic_area = therapeuticArea;
      if (status !== project.status) payload.status = status;
      if (progress !== project.progress) payload.progress = progress;
      if (stage) payload.stage_id = stage.numericId;

      await pipelineAPI.updateProjectPM(project.id, payload);
      showNotification('success', `${project.name} updated`);
      onSaved();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Update failed';
      showNotification('error', msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Edit Program" size="md">
      <div className="space-y-4">
        {/* Program Name */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Program Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Target */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Target</label>
          <input
            type="text"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Indication (free text) */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Indication</label>
          <input
            type="text"
            value={therapeuticArea}
            onChange={(e) => setTherapeuticArea(e.target.value)}
            placeholder="e.g. NSCLC, Breast Cancer, Asthma"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as PipelineProject['status'])}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Progress */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Progress — {progress}%
          </label>
          <input
            type="range"
            min={0}
            max={100}
            value={progress}
            onChange={(e) => setProgress(Number(e.target.value))}
            className="w-full accent-teal-500"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-100">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </Modal>
  );
};

export default EditProgramModal;
